import { paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type ConsentBody =
  paths['/program/{program_id}/{patient_id}/consent']['post']['requestBody']['content']['application/json'];
type ConsentResponse =
  paths['/program/{program_id}/{patient_id}/consent']['post']['responses']['201']['content']['application/json'];

interface IConsentMutationProps {
  patient_id: string;
  program_id: string;
}

// +++++++++++++++++ Create consent +++++++++++++++++

const createConsentRequest = async (
  props: ConsentBody & IConsentMutationProps,
) => {
  const { program_id, patient_id, ...body } = props;
  const response = await axiosInstance.post<ConsentResponse>(
    `/program/${program_id}/${patient_id}/consent`,
    body,
  );

  return response.data;
};

export const useCreateConsentMutation = ({
  patient_id,
  program_id,
}: IConsentMutationProps) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['createConsent'],
    mutationFn: (body: ConsentBody) =>
      createConsentRequest({ ...body, patient_id, program_id }),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: ['programConsent', patient_id, program_id],
      }),
  });

  return mutation;
};

// +++++++++++++++++ Update consent +++++++++++++++++

const updateConsentRequest = async (
  props: ConsentBody & IConsentMutationProps & { consent_id: string },
) => {
  const { program_id, patient_id, consent_id, ...body } = props;
  const response = await axiosInstance.patch<ConsentResponse>(
    `/program/${program_id}/${patient_id}/consent/${consent_id}`,
    body,
  );

  return response.data;
};

export const useUpdateConsentMutation = ({
  patient_id,
  program_id,
}: IConsentMutationProps) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['updateConsent'],
    mutationFn: (props: ConsentBody & { consent_id: string }) =>
      updateConsentRequest({ ...props, patient_id, program_id }),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: ['programConsent', patient_id, program_id],
      }),
  });

  return mutation;
};

// +++++++++++++++++ Delete consent +++++++++++++++++

const deleteConsentRequest = async ({
  program_id,
  patient_id,
  consent_id,
}: IConsentMutationProps & { consent_id: string }) => {
  await axiosInstance.delete(
    `/program/${program_id}/${patient_id}/consent/${consent_id}`,
  );
};

export const useDeleteConsentMutation = ({
  patient_id,
  program_id,
}: IConsentMutationProps) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deleteConsent'],
    mutationFn: ({ consent_id }: { consent_id: string }) =>
      deleteConsentRequest({ program_id, patient_id, consent_id }),
    onSuccess: () =>
      client.invalidateQueries({
        queryKey: ['programConsent', patient_id, program_id],
      }),
  });

  return mutation;
};
